/**
 * Boundary readers for JSON the app does not type at compile time.
 *
 * Three kinds of value arrive here untyped: hub API responses, the
 * `dashboard.layoutConfig` / `widgetConfig` blobs the editor writes, and the
 * automation configs carried over from earlier configs. The helpers below are
 * what stands between `unknown` and the rest of the app — every read checks the
 * runtime shape instead of casting to `any`, so a caller keeps a real type and a
 * malformed payload stays visible rather than spreading silently.
 *
 * Deliberately small: a type guard, a property read, and the primitive coercions.
 * Anything that needs to *describe* a payload belongs in a local interface at the
 * reader (or a zod schema), not here.
 */
import type { JsonValue } from '@hub/types/json';

/** JSON that has been confirmed to be an object. */
export type WireObject = Record<string, unknown>;

export function isWireObject(value: unknown): value is WireObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The object behind `value`, or undefined when `value` is not a plain object. */
export function wireObject(value: unknown): WireObject | undefined {
  return isWireObject(value) ? value : undefined;
}

/**
 * `value[key]` when `value` is an object, undefined otherwise.
 *
 * This is the workhorse: it replaces `raw.someField` on a value whose shape the
 * compiler cannot know, without widening the value to `any`.
 */
export function field(value: unknown, key: string): unknown {
  return isWireObject(value) ? value[key] : undefined;
}

/** The array behind `value`, or undefined when `value` is not one. */
export function wireArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

/** The string behind `value`, or undefined when `value` is not one. */
export function wireString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/** The boolean behind `value`, or undefined when `value` is not one. */
export function wireBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * The finite number behind `value`, or undefined.
 *
 * Numeric strings count (hub payloads carry both spellings); an empty string,
 * whitespace or a non-numeric one does not — `Number('')` is 0, which would turn
 * a missing field into a real value.
 */
export function wireNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** The string[] behind `value`, read element-wise; undefined when not an array. */
export function wireStringArray(value: unknown): string[] | undefined {
  const items = wireArray(value);
  if (!items) return undefined;
  return items.filter((item): item is string => typeof item === 'string');
}

/**
 * The JSON behind `value`, or undefined when it cannot be one.
 *
 * `JsonValue` is how the hub types its own JSON columns (`widgetConfig`,
 * `layoutConfig`, a device's `config`, a value's `currentValue`), and it is what
 * the model hands this app. This is the step from `unknown` to that column:
 * `null`, a string, a boolean and a finite number pass as themselves, an object
 * or array passes as it arrived — those payloads came off a fetch or a socket
 * frame, so their contents are JSON by the column's own contract, the same trust
 * `wireRow` states once — and anything else (a function, a symbol, a `Date`,
 * `NaN`) is not a JSON value and reads as `undefined` instead of being cast into
 * one.
 */
export function wireJsonValue(value: unknown): JsonValue | undefined {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'object') return value as JsonValue;
  return undefined;
}

/**
 * A JSON object whose values are all JSON — the shape of a config column
 * (`widgetConfig`, `layoutConfig`, a device's `config`) once it is known to be
 * an object.
 */
export type WireJsonBag = { [key: string]: JsonValue | undefined };

/**
 * The JSON *object* behind `value` — the bag a config column holds — or an empty
 * bag when the payload is not an object at all (a hub row with `config: null`
 * reads as `{}`, which is what every caller of this one defaults to anyway).
 */
export function wireJsonBag(value: unknown): WireJsonBag {
  return isWireObject(value) ? (value as WireJsonBag) : {};
}

/**
 * Spread a wire payload into a typed row.
 *
 * `overrides` are the fields the reader normalizes (alias spellings, parsed
 * dates, coerced numbers); every other key on `raw` passes through exactly as it
 * arrived, which is what the payloads of this app have always relied on. The
 * assertion is the one place that says "the rest of this row is the shape the
 * hub owns" — the trust a `raw: any` parameter used to state at every read, now
 * stated once, at the boundary, where a reader can see it.
 */
export function wireRow<T>(raw: WireObject, overrides: Partial<T>): T {
  return { ...raw, ...overrides } as T;
}
